import * as React from "react";
import {
  Activity,
  Compass,
  Gauge,
  Settings,
  Shield,
  Users,
  Layers,
  SlidersHorizontal,
  Tag,
  Key,
  Home,
  Pencil,
  Flag,
  Building2,
  Car,
  FileText,
  CreditCard,
  UserCheck,
  Wrench,
  Lock,
} from "lucide-react";
import type { NavigationItem } from "./navigation";
import { getLucideIcon } from "@/shared/lib/lucideIcons";
import { Tooltip } from "@/shared/ui/tooltip";
import type { useNavigate } from "@tanstack/react-router";

export const iconMap = {
  gauge: Gauge,
  activity: Activity,
  compass: Compass,
  settings: Settings,
  users: Users,
  shield: Shield,
  layers: Layers,
  sliders: SlidersHorizontal,
  tag: Tag,
  key: Key,
  home: Home,
  pencil: Pencil,
  flag: Flag,
  factory: Building2,
  building: Building2,
  car: Car,
  "file-text": FileText,
  wallet: CreditCard,
  handshake: UserCheck,
  wrench: Wrench,
  lock: Lock,
} as const;

export function getIcon(item: NavigationItem) {
  if (!item.icon) {
    return null;
  }

  const IconComponent = iconMap[item.icon as keyof typeof iconMap];
  if (IconComponent) {
    return <IconComponent aria-hidden="true" className="h-4 w-4" />;
  }

  const LucideIcon = getLucideIcon(item.icon);
  return LucideIcon ? (
    <LucideIcon aria-hidden="true" className="h-4 w-4" />
  ) : null;
}

export function withTooltip(
  collapsed: boolean,
  item: NavigationItem,
  node: React.ReactNode
) {
  if (!collapsed) {
    return node;
  }

  return (
    <Tooltip
      key={`tooltip-${item.id}`}
      content={item.label}
      description={item.description}
      side="right"
      align="center"
      delayDuration={120}
    >
      {node}
    </Tooltip>
  );
}

export function convertSearchParams(
  search?: Record<string, string | number | boolean | undefined>
): Record<string, string | undefined> {
  if (!search) return {};

  const params: Record<string, string | undefined> = {};
  Object.entries(search).forEach(([key, value]) => {
    if (value !== undefined) {
      params[key] = String(value);
    }
  });
  return params;
}

export function navigateToItem(
  item: NavigationItem,
  navigate: ReturnType<typeof useNavigate>,
  onNavigate?: () => void
) {
  const searchParams = convertSearchParams(item.target.search);

  navigate({
    to: item.target.to,
    params: item.target.params,
    search: searchParams,
    replace: true,
  });

  onNavigate?.();
}
